import { action, observable } from "mobx";
import { FormulaFormVM } from "../template/FormulaFormVM";
import { HydraulicFormulaController } from "cd-eft/services/eftSetApi";
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";
import { DryHydrantRequestDto, DryHydrantResponseDto } from "cd-eft/_codegen_/openapi-generator/eft-set";


interface ISelection {
  name: string,
  value: string | number
  optionDisabled?: boolean
}
interface IDryHydrantForm {
  hydrantCenterlineElev: string,
  inletCenterlineElev: string,
  normalWaterElev: string,
  pumpCenterlineElev: string,
  hazenWilliamsCoeff: string,
  numberOf45Elbows: string,
  numberOf90Elbows: string,
  pipeDiameter: string,
  totalPipeLength: string,
  waterNeeded: string
}

export class DryHydrantVM extends FormulaFormVM {
  readonly formLabelsForExport;
  readonly resultLabelsForExport;
  constructor(props) {
    super(props);
    this.helpLink = 'https://usdagcc.sharepoint.com/:b:/r/sites/FPAC-NRCS-EngineeringPlanningAnalysisandDesignSoftwareEPAD/Shared%20Documents/General/NETS-Support%20Documents/Formulas%20Documentation/Help%20Select%20C%20Value.pdf?csf=1&web=1&e=DcxebH';
    this.formLabelsForExport = {
      waterNeeded: 'Water Needed (gpm)',
      pumpCenterlineElev: "Pump Centerline Elevation (ft)",
      hydrantCenterlineElev: "Hydrant Centerline Elevation (ft)",
      normalWaterElev: "Normal Water Elevation (ft)",
      inletCenterlineElev: "Inlet Centerline Elevation (ft)",
      totalPipeLength: "Total Pipe Length (ft)",
      pipeDiameter: "Pipe Diameter (in)",
      hazenWilliamsCoeff: "Hazen-Williams Coefficient (C)",
      numberOf90Elbows: "Number of 90° Elbows",
      numberOf45Elbows: "Number of 45° Elbows",
    };
    this.resultLabelsForExport = {
      headLoss: 'Total Head Loss (ft)',
      materials: 'Materials Needed'
    };

  }

  // Expand with onChange handlers, onBlur validation, Submit handler, on submit validation, individual variables, etc.
  @observable dryHydrantForm: IDryHydrantForm = {
    hydrantCenterlineElev: "0",
    inletCenterlineElev: "0",
    normalWaterElev: "0",
    pumpCenterlineElev: "0",
    hazenWilliamsCoeff: "0",
    numberOf45Elbows: "0",
    numberOf90Elbows: "0",
    pipeDiameter: "0",
    totalPipeLength: "0",
    waterNeeded: "0"
  }
  @observable dryHydrantResults: DryHydrantResponseDto | undefined;
  @observable dryHydrantFormInputSnapshot: IDryHydrantForm | undefined = undefined
  @observable hasResults: boolean = false;
  @observable pipeDiameterOptions: ISelection[] = [
    {
      name: "6",
      value: 6,
    },
    {
      name: "8",
      value: 8,
    },
    {
      name: "10",
      value: 10,
    },
    {
      name: "12",
      value: 12,
    },
  ];

  @observable formValidation = {
    hasError: true,
    pumpCenterlineElevation: false,
    hydrantCenterlineElevation: false,
    normalWaterElevation: false,
    waterNeeded: false,
    floorElevation: false,
    numberof45Elbows: false,
    totalPipeLength: false,
    pipeDiameter: false,
    numberof90Elbows: false,
    inletCenterlineElevation: false,
    hazenWilliamsCoefficient: false,
    formErrors: {
      normalWaterElevation: '',
      waterNeeded: '',
      pumpCenterlineElevation: '',
      hydrantCenterlineElevation: '',
      hazenWilliamsCoefficient: '',
      inletCenterlineElevation: '',
      totalPipeLength: '',
      pipeDiameter: '',
      floorElevation: '',
      numberof45Elbows: '',
      numberof90Elbows: '',
    },
  };

  @action.bound DryHydrantErrorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage?.length === 0 ? '' : ' usa-input--error';
  }
  @action.bound setFixedValue(name, fixed) {
    const event1 = {
     target: {
     name: name,
     value: fixed
     }
     }
    this.handleDryHydrantForm(event1, true);
     }
  @action.bound handleDryHydrantForm(event, within?: boolean) {
    this.disableComputeBtn = false
    const { name, value } = event.target;
    if (!within && value.length > 7) {
      return;
    }
    switch (name) {
      case 'pump-centerline-elevation':
        this.dryHydrantForm.pumpCenterlineElev = value;
        break;
      case 'water-needed':
        this.dryHydrantForm.waterNeeded = value;
        break;
      case 'hydrant-centerline-elevation':
        this.dryHydrantForm.hydrantCenterlineElev = value;
        break;
      case 'normal-water-elevation':
        this.dryHydrantForm.normalWaterElev = value;
        break;
      case 'inlet-centerline-elevation':
        this.dryHydrantForm.inletCenterlineElev = value;
        break;
      case 'total-pipe-length':
        this.dryHydrantForm.totalPipeLength = value;
        break;
      case 'pipe-diameter':
        this.dryHydrantForm.pipeDiameter = value;
        break;
      case 'hazen-williams-coefficient':
        this.dryHydrantForm.hazenWilliamsCoeff = value;
        break;
      case 'ninety-degree-elbow':
        this.dryHydrantForm.numberOf90Elbows = value;
        break;
      case 'forty-five-degree-elbow':
        this.dryHydrantForm.numberOf45Elbows = value;
        break;
    }
  }

  @action.bound async validateInput(name: string, value) {
    let val = 0;
    if (name.includes('elbow')) {
      // elbows should be integers, not decimals. Tuncates to int.
      val = parseInt(value);
    }
    else {
      val = this.roundingHelperNumber(parseFloat(value), 2);
    }
    if(name !== "pipe-diameter") this.setFixedValue(name, val);

    switch (name) {
      case 'water-needed':
        if (value === '' || Number.isNaN(value) || val < 250 || val > 2000) {
          this.formValidation.waterNeeded = true;
          this.formValidation.formErrors.waterNeeded = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Formula_08');
        }
        else {
          this.formValidation.waterNeeded = false;
          this.formValidation.formErrors.waterNeeded = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'pump-centerline-elevation':
        if (value === '' || Number.isNaN(value) || val <= 0) {
          this.formValidation.pumpCenterlineElevation = true;
          this.formValidation.formErrors.pumpCenterlineElevation = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.pumpCenterlineElev.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.pumpCenterlineElevation = false;
          this.formValidation.formErrors.pumpCenterlineElevation = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'hydrant-centerline-elevation':
        if (value === '' || Number.isNaN(value) || val <= 0) {
          this.formValidation.hydrantCenterlineElevation = true;
          this.formValidation.formErrors.hydrantCenterlineElevation = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.hydrantCenterlineElev.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.hydrantCenterlineElevation = false;
          this.formValidation.formErrors.hydrantCenterlineElevation = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'normal-water-elevation':
        if (value === '' || Number.isNaN(value) || val <= 0) {
          this.formValidation.normalWaterElevation = true;
          this.formValidation.formErrors.normalWaterElevation = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.normalWaterElev.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.normalWaterElevation = false;
          this.formValidation.formErrors.normalWaterElevation = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'inlet-centerline-elevation':
        if (value === '' || Number.isNaN(value) || val === 0) {
          this.formValidation.inletCenterlineElevation = true;
          this.formValidation.formErrors.inletCenterlineElevation = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.inletCenterlineElev.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.inletCenterlineElevation = false;
          this.formValidation.formErrors.inletCenterlineElevation = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'total-pipe-length':
        if (value === '' || Number.isNaN(value) || val <= 0) {
          this.formValidation.totalPipeLength = true;
          this.formValidation.formErrors.totalPipeLength = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.totalPipeLength.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.totalPipeLength = false;
          this.formValidation.formErrors.totalPipeLength = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'pipe-diameter':
        if (value === '' || Number.isNaN(value)) {
          this.formValidation.pipeDiameter = true;
          this.formValidation.formErrors.pipeDiameter = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_09', undefined, this.formLabelsForExport.pipeDiameter.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.pipeDiameter = false;
          this.formValidation.formErrors.pipeDiameter = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'hazen-williams-coefficient':
        if (value === '' || Number.isNaN(value) || val <= 0) {
          this.formValidation.hazenWilliamsCoefficient = true;
          this.formValidation.formErrors.hazenWilliamsCoefficient = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.hazenWilliamsCoeff.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.hazenWilliamsCoefficient = false;
          this.formValidation.formErrors.hazenWilliamsCoefficient = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'ninety-degree-elbow':
        if (val < 0) {
          this.formValidation.numberof90Elbows = true;
          this.formValidation.formErrors.numberof90Elbows = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_14', undefined, this.formLabelsForExport.numberOf90Elbows.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.numberof90Elbows = false;
          this.formValidation.formErrors.numberof90Elbows = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'forty-five-degree-elbow':
        if (val < 0) {
          this.formValidation.numberof45Elbows = true;
          this.formValidation.formErrors.numberof45Elbows = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_14', undefined, this.formLabelsForExport.numberOf45Elbows.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.numberof45Elbows = false;
          this.formValidation.formErrors.numberof45Elbows = '';
          this.formValidation.hasError = false;
        }
        break;
    }
  }
  get validateRequiredInputs() {
    if (!this.formValidation.waterNeeded && !this.formValidation.pumpCenterlineElevation &&
      !this.formValidation.hydrantCenterlineElevation && !this.formValidation.normalWaterElevation && !this.formValidation.totalPipeLength
      && !this.formValidation.pipeDiameter && !this.formValidation.hazenWilliamsCoefficient && !this.formValidation.inletCenterlineElevation
      && !this.formValidation.numberof90Elbows && !this.formValidation.numberof45Elbows
    ) {
      return true
    }
    return false
  }
  async validateReqInput() {
    await this.validateInput("water-needed", this.dryHydrantForm.waterNeeded);
    await this.validateInput("pump-centerline-elevation", this.dryHydrantForm.pumpCenterlineElev);
    await this.validateInput("hydrant-centerline-elevation", this.dryHydrantForm.hydrantCenterlineElev);
    await this.validateInput("normal-water-elevation", this.dryHydrantForm.normalWaterElev);
    await this.validateInput("total-pipe-length", this.dryHydrantForm.totalPipeLength);
    await this.validateInput("pipe-diameter", this.dryHydrantForm.pipeDiameter);
    await this.validateInput("hazen-williams-coefficient", this.dryHydrantForm.hazenWilliamsCoeff);
    await this.validateInput("inlet-centerline-elevation", this.dryHydrantForm.inletCenterlineElev);
    await this.validateInput("forty-five-degree-elbow", this.dryHydrantForm.numberOf45Elbows);
    await this.validateInput("ninety-degree-elbow", this.dryHydrantForm.numberOf90Elbows);
  }
  @action.bound  async computeDryHydrant() {
    this.disableComputeBtn = true;
    this.disablePrintBtn = true;
    MessageCenter.blockCalls(['Common_26'])
    await this.validateReqInput();
    if (this.validateRequiredInputs) {
      try {
        const requestPayload: DryHydrantRequestDto = {
          dryHydrantElevationDto: {
            hydrantCenterlineElev: parseFloat(this.dryHydrantForm.hydrantCenterlineElev),
            inletCenterlineElev: parseFloat(this.dryHydrantForm.inletCenterlineElev),
            normalWaterElev: parseFloat(this.dryHydrantForm.normalWaterElev),
            pumpCenterlineElev: parseFloat(this.dryHydrantForm.pumpCenterlineElev)
          },
          pipelineDto: {
            hazenWilliamsCoeff: parseFloat(this.dryHydrantForm.hazenWilliamsCoeff),
            numberOf45Elbows: parseFloat(this.dryHydrantForm.numberOf45Elbows),
            numberOf90Elbows: parseFloat(this.dryHydrantForm.numberOf90Elbows),
            pipeDiameter: parseFloat(this.dryHydrantForm.pipeDiameter),
            totalPipeLength: parseFloat(this.dryHydrantForm.totalPipeLength)
          },
          waterNeeded: parseFloat(this.dryHydrantForm.waterNeeded)
        }
        const response = await HydraulicFormulaController.calculateDryHydrantUsingPOST(requestPayload)
        const shortCode = response?.data?.errorResponse?.errorCode?.replace(/[123]_[a-zA-Z]+_[a-zA-Z]+_/, '');
        if (response && response.data) {
          if (response.data.validationPass) {
            MessageCenter.resetMessageCenter();
            this.hasResults = true;
            this.disablePrintBtn = false;
            this.dryHydrantResults = response.data;
            this.dryHydrantFormInputSnapshot = { ...this.dryHydrantForm }
          } else if (response?.data?.errorResponse?.errorMessage != undefined && response?.data?.errorResponse?.errorType != undefined) {
            MessageCenter.resetMessageCenter();
            MessageCenter.addNotification(response?.data?.errorResponse?.errorMessage, response?.data?.errorResponse?.errorType, shortCode);
            this.hasResults = false;
            this.disablePrintBtn = true;
          }
        };
      } catch (error) {
        console.error(error)
      }
      finally {
        this.disableComputeBtn = false;
      }
    } else {
      MessageCenter.resetMessageCenter();
      await MessageCenter.addNotificationFromCode('Common_26');
      this.hasResults = false;
    }
    MessageCenter.unblockCalls();
  }
  @action.bound launchAndExport() {
    this.disablePrintBtn = true;
    this.exportedInputs = [];
    this.exportedResults = [];
    for (const input in this.dryHydrantFormInputSnapshot) {
      this.exportedInputs.push({
        label: this.formLabelsForExport[input],
        value: this.dryHydrantFormInputSnapshot[input]
      })
    }
    for (const result in this.dryHydrantResults) {
      if (result !== 'errorResponse' || 'validationPass') {
        const label = this.resultLabelsForExport[result];
        const value = this.dryHydrantResults[result];
        if (label && value !== undefined) {
          this.exportedResults.push({
            label: this.resultLabelsForExport[result],
            value: this.dryHydrantResults[result],
          });
        }
      }
    }
    this.printFormula();
    this.disablePrintBtn = false;
  }

  @action.bound initializeDefaultValues(): void {
    this.disablePrintBtn = true
    this.dryHydrantForm = {
      waterNeeded: this.roundingHelperString(this.defaultValues.waterNeeded, 2),
      pumpCenterlineElev: this.roundingHelperString(this.defaultValues.dryHydrantElevationDto.pumpCenterlineElev, 2),
      hydrantCenterlineElev: this.roundingHelperString(this.defaultValues.dryHydrantElevationDto.hydrantCenterlineElev, 2),
      normalWaterElev: this.roundingHelperString(this.defaultValues.dryHydrantElevationDto.normalWaterElev, 2),
      inletCenterlineElev: this.roundingHelperString(this.defaultValues.dryHydrantElevationDto.inletCenterlineElev, 2),
      pipeDiameter: this.defaultValues.pipelineDto.pipeDiameter,
      hazenWilliamsCoeff: this.roundingHelperString(this.defaultValues.pipelineDto.hazenWilliamsCoeff, 2),
      numberOf45Elbows: this.defaultValues.pipelineDto.numberOf45Elbows,
      numberOf90Elbows: this.defaultValues.pipelineDto.numberOf90Elbows,
      totalPipeLength: this.roundingHelperString(this.defaultValues.pipelineDto.totalPipeLength, 2)
    }
  }
  @action.bound handleOnKeyDown(event) {
    ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();
  }

}